import React, { useEffect, useRef, useState } from "react";
import {
  Paper,
  Typography,
  Box,
  Divider,
  TableCell,
  Tooltip,
  alpha,
} from "@mui/material";
import ScheduleNotes from "../../../Schedules/ScheduleNotes";
import ScheduleContainer from "../../../Schedules/utils/ScheduleContainer";
import ScheduleHeader from "../../../App/ScheduleHeader";
import SectionBox from "../../../Schedules/utils/SectionBox";
import CommonTable, { ReadOnlyCell } from "../../../utils/CommonTable";
import { TextFieldForName } from "../../../utils/TextField/TextField";
import { formatAmount } from "../../../../utils/hooks/dataHandler";
import { calculateAllocationExpenses } from "./calculation";
import CommonCalculator from "../../../../builders/calculator/common";
import CappedCommonExpenseBlock from "./components/CappedCommonExpenseBlock";
import DeductibleExpenseSummary from "./components/DeductibleExpenseSummary";
import PdfGenerator from "../Pdf/AllocationExpensePdf";
import InfoIcon from "@mui/icons-material/Info";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";

function CalculationRow({ label, amount, variant = "default", tooltip }) {
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        py: 1,
        px: 2,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Typography
          sx={{
            fontWeight: variant === "primary" ? 600 : 500,
            color: "text.primary",
          }}
        >
          {label}
        </Typography>
        {tooltip && (
          <Tooltip title={tooltip} arrow>
            <HelpOutlineIcon
              sx={{ fontSize: 16, color: "text.disabled", cursor: "help" }}
            />
          </Tooltip>
        )}
      </Box>
      <Typography
        sx={{
          fontWeight: variant === "primary" ? 600 : 500,
          color: "text.primary",
        }}
      >
        {formatAmount(amount)}
      </Typography>
    </Box>
  );
}

const getTableColumns = (handleChange, setRows) => [
  {
    key: "description",
    label: "Description",
    headerAlign: "left",
    render: (row, index) => (
      <ReadOnlyCell value={row.description} width='280px' textAlign='left' />
    ),
  },
  {
    key: "amount",
    label: "Amount",
    headerAlign: "right",
    colAlign: "right",
    render: (row, index) => (
      <ReadOnlyCell
        value={formatAmount(row.amount)}
        width='100%'
        textAlign='right'
      />
    ),
  },
  {
    key: "reference",
    label: "Reference",
    headerAlign: "center",
    render: (row, index) => (
      <TextFieldForName
        value={row?.reference}
        onChange={handleChange}
        reference={{
          setRows,
          rowId: row.rowId,
          field: "reference",
        }}
      />
    ),
  },
  {
    key: "source",
    label: "Sch",
    headerAlign: "center",
    render: (row) => <>{row.source?.sch_no || ""}</>,
  },
];

const AllocationExpenseSchedule = ({
  company,
  schedule,
  scheduleDetails,
  saveSchedule,
  deleteSchedule,
  canEditSchedule = true,
  canDeleteSchedule = true,
}) => {
  const [schName, setSchName] = useState("Allocation Of Expenses");
  const [notes, setNotes] = useState("");
  const [reviewNotes, setReviewNotes] = useState("");

  const [incomeRows, setIncomeRows] = useState([]);
  const [statutoryExpensesRows, setStatutoryExpensesRows] = useState([]);
  const [commonExpensesRows, setCommonExpensesRows] = useState([]);

  // Total income is treated as an init-only value (computed once on first load / refresh).
  const [incomeTotal, setIncomeTotal] = useState(0);
  const [cappedRate, setCappedRate] = useState(0);
  const [deductibleRowOffsets, setDeductibleRowOffsets] = useState({});

  const didInitFromScheduleDetailsRef = useRef(false);

  const rowOffsetKey = (row, index) =>
    row?.rowId != null ? String(row.rowId) : `__idx_${index}`;

  const clampRowOffset = (v) => {
    const n = parseInt(v, 10);
    if (Number.isNaN(n)) return 0;
    return Math.max(-1, Math.min(1, n));
  };

  const handleSave = () => {
    schedule.name = schName;
    schedule.notes = notes;
    schedule.reviewNotes = reviewNotes;

    saveSchedule(schedule, {
      incomeRows: incomeRows,
      statutoryExpensesRows: statutoryExpensesRows,
      commonExpensesRows: commonExpensesRows,
      cappedRate: cappedRate,
      expenseCappedAmount: effectiveExpenseCappedAmount,
      deductibleRowOffsets: deductibleRowOffsets,
      deductibleAllocationRows: deductibleAllocationRowsForDisplay,
      doc: JSON.stringify({
        incomeRows,
        statutoryExpensesRows,
        commonExpensesRows,
        cappedRate,
        expenseCappedAmount: effectiveExpenseCappedAmount,
        cappedVsCommon,
        finalAllowedExpenses,
        deductibleAllocationRows: deductibleAllocationRowsForDisplay,
        totalDeductibleExpenses: displayedDeductibleTotal,
        notes,
        reviewNotes,
      }),
    });
  };

  const handleReferenceChange = (value, params) => {
    const { rowId, field, setRows } = params;
    setRows((prevRows) => {
      const newRows = [...prevRows];
      newRows.find((row) => row.rowId === rowId)[field] = value;
      return newRows;
    });
  };

  const handleCappedAt = (value) => {
    if (value === "" || value >= 0) {
      setCappedRate(value);
    }
  };

  useEffect(() => {
    // init
    if (schedule) {
      setSchName(schedule.name);
      setNotes(schedule.notes);
      setReviewNotes(schedule.reviewNotes);
    }

    if (!scheduleDetails || didInitFromScheduleDetailsRef.current) return;

    didInitFromScheduleDetailsRef.current = true;

    const initIncomeRows = (scheduleDetails.dataRows?.incomeRows || []).map(
      (row) => ({
        ...row,
        amount: Math.abs(Number(row.amount)),
      })
    );
    const initCappedRate = scheduleDetails.dataRows?.cappedRate || 5;

    setIncomeRows(initIncomeRows);
    setStatutoryExpensesRows(
      scheduleDetails.dataRows?.statutoryExpensesRows || []
    );
    setCommonExpensesRows(scheduleDetails.dataRows?.commonExpensesRows || []);
    setCappedRate(initCappedRate);

    const savedOffsets = scheduleDetails.dataRows?.deductibleRowOffsets;
    if (savedOffsets && typeof savedOffsets === "object") {
      setDeductibleRowOffsets(savedOffsets);
    } else {
      setDeductibleRowOffsets({});
    }

    const initIncomeTotal = CommonCalculator.arraySum(initIncomeRows);
    setIncomeTotal(initIncomeTotal);
  }, [schedule, scheduleDetails]);

  const statutoryExpensesTotal = React.useMemo(() => {
    return statutoryExpensesRows.reduce(
      (sum, row) => sum + (parseFloat(row.amount) || 0),
      0
    );
  }, [statutoryExpensesRows]);

  const commonExpensesTotal = React.useMemo(() => {
    return commonExpensesRows.reduce(
      (sum, row) => sum + (parseFloat(row.amount) || 0),
      0
    );
  }, [commonExpensesRows]);

  const grossInvestmentIncomeInfo = React.useMemo(() => {
    if (incomeRows.length > 0 && Number.isFinite(incomeTotal)) {
      return {
        label: "P&L Investment Income",
        amount: incomeTotal,
      };
    }

    return {
      label: "Total income",
      amount: incomeTotal,
    };
  }, [incomeRows, incomeTotal]);

  const isServiceIncomeRow = (row) => {
    const tag = (row.taggedTo ?? "").toString().trim();
    const scheduleType = (row.scheduleType ?? "").toString();
    const incomeType = (row.incomeType ?? "").toString();
    const sourceRowType = (row.source?.row_type ?? "").toString();

    return (
      ["ServiceIncome", "Service_Income", "Service Income"].includes(
        scheduleType
      ) ||
      ["ServiceIncome", "Service_Income", "Service Income"].includes(
        incomeType
      ) ||
      sourceRowType === "service_income" ||
      incomeType === "service_income" ||
      /service\s*income/i.test(tag)
    );
  };

  const hasServiceIncome = React.useMemo(
    () => incomeRows.some(isServiceIncomeRow),
    [incomeRows]
  );

  const serviceIncomeTotal = React.useMemo(() => {
    return incomeRows.reduce(
      (sum, row) =>
        sum + (isServiceIncomeRow(row) ? parseFloat(row.amount) || 0 : 0),
      0
    );
  }, [incomeRows]);

  const nonServiceIncomeTotal = React.useMemo(() => {
    return Math.max((incomeTotal || 0) - serviceIncomeTotal, 0);
  }, [incomeTotal, serviceIncomeTotal]);

  const commonExpenseCapBase = hasServiceIncome
    ? nonServiceIncomeTotal
    : incomeTotal;

  const effectiveExpenseCappedAmount = React.useMemo(() => {
    const cappedAmount = Math.abs(
      calculateAllocationExpenses(commonExpenseCapBase, cappedRate)
    );
    return commonExpensesTotal < 0 ? -cappedAmount : cappedAmount;
  }, [commonExpenseCapBase, cappedRate, commonExpensesTotal]);

  const commonExpenseCapBaseInfo = React.useMemo(() => {
    if (hasServiceIncome) {
      return {
        label: "Non-service Investment Income",
        amount: nonServiceIncomeTotal,
      };
    }

    return grossInvestmentIncomeInfo;
  }, [grossInvestmentIncomeInfo, hasServiceIncome, nonServiceIncomeTotal]);

  const cappedVsCommon = React.useMemo(() => {
    const capped = Math.abs(effectiveExpenseCappedAmount);
    const common = Math.abs(commonExpensesTotal);
    const deductionComesFromCap = capped <= common;

    return {
      deductionAmount: deductionComesFromCap
        ? effectiveExpenseCappedAmount
        : commonExpensesTotal,
      deductionComesFromCap,
    };
  }, [effectiveExpenseCappedAmount, commonExpensesTotal]);

  const totalAllowedExpenses = React.useMemo(() => {
    if (!incomeTotal || !Number.isFinite(statutoryExpensesTotal)) {
      return 0;
    }

    const cappedDeduction = cappedVsCommon.deductionAmount;

    return incomeRows.reduce((sum, row) => {
      const incomeAmount = parseFloat(row.amount) || 0;
      const share = incomeAmount / incomeTotal;
      const allowedPool =
        statutoryExpensesTotal +
        (isServiceIncomeRow(row) ? commonExpensesTotal : cappedDeduction);

      return sum + share * allowedPool;
    }, 0);
  }, [
    incomeRows,
    incomeTotal,
    statutoryExpensesTotal,
    commonExpensesTotal,
    cappedVsCommon.deductionAmount,
  ]);

  const deductibleAllocationRows = React.useMemo(() => {
    if (!incomeTotal || !Number.isFinite(statutoryExpensesTotal)) {
      return [];
    }

    const cappedDeduction = cappedVsCommon.deductionAmount;

    let sumBeforeLast = 0;
    const rows = incomeRows.map((row, index) => {
      const isLast = index === incomeRows.length - 1;

      const incomeAmount = parseFloat(row.amount) || 0;
      const share =
        incomeTotal === 0 ? 0 : parseFloat(incomeAmount / incomeTotal);

      const isServiceIncome = isServiceIncomeRow(row);
      const commonExpensesUsed = isServiceIncome
        ? commonExpensesTotal
        : cappedDeduction;
      const allowedPool = statutoryExpensesTotal + commonExpensesUsed;
      let deductibleAmount = parseFloat((share * allowedPool).toFixed(2));
      const calculatedDeductibleAmount = deductibleAmount;

      if (
        isLast &&
        Number.isFinite(totalAllowedExpenses) &&
        (totalAllowedExpenses < 0
          ? sumBeforeLast >= totalAllowedExpenses
          : sumBeforeLast <= totalAllowedExpenses)
      ) {
        const remaining = totalAllowedExpenses - sumBeforeLast;

        deductibleAmount =
          deductibleAmount < 0
            ? Math.min(0, Math.max(deductibleAmount, remaining))
            : Math.max(0, Math.min(deductibleAmount, remaining));
      } else {
        sumBeforeLast += Math.round(deductibleAmount);
      }

      deductibleAmount = Math.abs(deductibleAmount);

      return {
        rowId: row.rowId,
        description: row.description || "Untitled item",
        incomeAmount,
        share,
        taggedTo: row.taggedTo,
        deductibleAmount,
        original_amount: row?.source?.original_amount || null,
        working: {
          incomeAmount,
          totalIncome: incomeTotal,
          share,
          statutoryExpenses: statutoryExpensesTotal,
          commonExpenses: commonExpensesTotal,
          cappedCommonExpenses: cappedDeduction,
          commonExpensesUsed,
          expensePoolUsed: allowedPool,
          calculatedDeductibleAmount,
          deductibleAmount,
          incomeType: isServiceIncome ? "Service Income" : "Investment Income",
          commonExpenseTreatment: isServiceIncome
            ? "Full common expenses"
            : "Capped common expenses",
        },
      };
    });
    return rows;
  }, [
    incomeRows,
    incomeTotal,
    statutoryExpensesTotal,
    commonExpensesTotal,
    cappedVsCommon.deductionAmount,
    totalAllowedExpenses,
  ]);

  const finalAllowedExpenses = React.useMemo(() => {
    return Math.round(totalAllowedExpenses || 0);
  }, [totalAllowedExpenses]);

  const deductibleAllocationRowsForDisplay = React.useMemo(() => {
    return deductibleAllocationRows.map((row, index) => {
      const key = rowOffsetKey(row, index);
      const o = clampRowOffset(deductibleRowOffsets[key]);
      const base = Math.round(row.deductibleAmount ?? 0);
      const roundedDeductibleAmount = base + o;
      return {
        ...row,
        deductibleAmount: roundedDeductibleAmount,
        working: {
          ...(row.working || {}),
          roundedDeductibleAmount,
          roundingOffset: o,
        },
      };
    });
  }, [deductibleAllocationRows, deductibleRowOffsets]);

  const displayedDeductibleTotal = React.useMemo(() => {
    return deductibleAllocationRowsForDisplay.reduce(
      (sum, row) => sum + (row.deductibleAmount ?? 0),
      0
    );
  }, [deductibleAllocationRowsForDisplay]);

  const displayAndFinalTotalMismatch = React.useMemo(() => {
    return (
      Math.abs(displayedDeductibleTotal) !== Math.abs(finalAllowedExpenses)
    );
  }, [displayedDeductibleTotal, finalAllowedExpenses]);

  return (
    <Box sx={{ padding: 4 }}>
      <ScheduleHeader
        {...{
          title: schedule.type,
          companyName: company.name,
          companyType: company.companyType,
          uen: company.uen,
          yearOfAssessment: company.yearOfAssessment,
          financialYearStart: company.financialYearStart,
          financialYearEnd: company.financialYearEnd,
          handleSave,
          deleteSchedule,
          canEditSchedule,
          canDeleteSchedule,
        }}
      >
        <PdfGenerator
          schedule={schedule}
          company={company}
          incomeRows={incomeRows}
          statutoryExpensesRows={statutoryExpensesRows}
          commonExpensesRows={commonExpensesRows}
          cappedRate={cappedRate}
          expenseCappedAmount={effectiveExpenseCappedAmount}
          cappedVsCommon={cappedVsCommon}
          finalAllowedExpenses={finalAllowedExpenses}
          deductibleAllocationRows={deductibleAllocationRowsForDisplay}
          deductibleRowOffsets={deductibleRowOffsets}
          totalDeductibleExpenses={displayedDeductibleTotal}
          notes={notes}
          reviewNotes={reviewNotes}
        />
      </ScheduleHeader>
      <br />

      <ScheduleContainer
        key={schedule.id}
        id={schedule.id}
        schedule={schedule}
        schName={schName}
        allowedToEditSchName
        updateHeading={(newTitle) => {
          schedule.name = newTitle;
          setSchName(newTitle);
        }}
      >
        {(fullScreenMode) => (
          <>
            <SectionBox title='Statutory Expenses'>
              <CommonTable
                rows={statutoryExpensesRows}
                columns={getTableColumns(
                  handleReferenceChange,
                  setStatutoryExpensesRows
                )}
              >
                <TableCell
                  align='right'
                  sx={{
                    fontWeight: "bold",
                  }}
                >
                  Total Statutory Expenses
                </TableCell>
                <TableCell align='right' sx={{ fontWeight: "bold" }}>
                  {formatAmount(statutoryExpensesTotal)}
                </TableCell>
                <TableCell colSpan={2}></TableCell>
              </CommonTable>
            </SectionBox>

            <SectionBox title='Common Expenses'>
              <CommonTable
                rows={commonExpensesRows}
                columns={getTableColumns(
                  handleReferenceChange,
                  setCommonExpensesRows
                )}
              >
                <TableCell
                  align='right'
                  sx={{
                    fontWeight: "bold",
                  }}
                >
                  Total Common Expenses
                </TableCell>
                <TableCell align='right' sx={{ fontWeight: "bold" }}>
                  {formatAmount(commonExpensesTotal)}
                </TableCell>
                <TableCell colSpan={2}></TableCell>
              </CommonTable>
            </SectionBox>

            <Paper
              elevation={0}
              sx={{
                p: 4,
                border: "1px solid",
                borderColor: "divider",
                bgcolor: "background.paper",
                borderRadius: 2,
                boxShadow: (theme) => theme.shadows[2], // 增加一点阴影提升层级感
              }}
            >
              {/* 1. 标题区域：清晰明了 */}
              <Box sx={{ mb: 4 }}>
                <Typography
                  variant='h6'
                  fontWeight={700}
                  color='text.primary'
                  gutterBottom
                >
                  Final Allowance Calculation
                </Typography>
                <Typography variant='body2' color='text.secondary'>
                  Breakdown of deductible expenses based on statutory limits and
                  investment income caps.
                </Typography>
              </Box>

              {/* 2. 输入/控制区域 (保持原样，但确保它不干扰下方的只读计算展示) */}
              <Box
                sx={{ mb: 4, pb: 4, borderBottom: 1, borderColor: "divider" }}
              >
                <CappedCommonExpenseBlock
                  cappedRate={cappedRate}
                  expenseCappedAmount={effectiveExpenseCappedAmount}
                  handleCappedAt={handleCappedAt}
                  grossInvestmentIncomeInfo={commonExpenseCapBaseInfo}
                  incomeRows={incomeRows}
                />
              </Box>

              {/* 3. 计算展示区域：采用清晰的 "A - B = C" 结构 */}
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {/* Row 1: Statutory Total (Starting Point) */}
                <CalculationRow
                  label='Statutory Total Expenses'
                  amount={statutoryExpensesTotal}
                  variant='primary'
                  tooltip='The total expenses calculated based on statutory rates before any caps.'
                />

                {/* Row 2: The Deduction (The Logic Core) */}
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 2, px: 2 }}
                >
                  <Typography
                    sx={{
                      color: "text.disabled",
                      fontWeight: 700,
                      fontSize: "1.2rem",
                    }}
                  >
                    +
                  </Typography>
                  <Box sx={{ flex: 1 }}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "baseline",
                      }}
                    >
                      <Typography
                        sx={{ fontWeight: 600, color: "text.secondary" }}
                      >
                        Allowable Deduction
                        <Tooltip
                          title={
                            cappedVsCommon.deductionComesFromCap
                              ? `Capped at ${cappedRate}% of ${commonExpenseCapBaseInfo.label}`
                              : "Based on actual Total Common Expenses (below cap)"
                          }
                        >
                          <InfoIcon
                            sx={{
                              fontSize: 16,
                              ml: 1,
                              verticalAlign: "middle",
                              cursor: "help",
                            }}
                          />
                        </Tooltip>
                      </Typography>
                      <Typography
                        sx={{ fontWeight: 600, color: "text.secondary" }}
                      >
                        {formatAmount(cappedVsCommon.deductionAmount)}
                      </Typography>
                    </Box>
                    <Typography
                      variant='caption'
                      color='text.disabled'
                      sx={{ display: "block", mt: 0.5 }}
                    >
                      {cappedVsCommon.deductionComesFromCap
                        ? `Limited to ${cappedRate}% of ${commonExpenseCapBaseInfo.label}`
                        : "Full common expenses applied"}
                    </Typography>
                    {hasServiceIncome && (
                      <Typography
                        variant='caption'
                        color='text.disabled'
                        sx={{ display: "block", mt: 0.25 }}
                      >
                        Service income is allocated using full common expenses;
                        this cap applies only to non-service investment income.
                      </Typography>
                    )}
                  </Box>
                </Box>

                <Divider sx={{ my: 1, borderStyle: "dashed" }} />

                {/* Row 3: Final Result (Highlight) */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    p: 2.5,
                    bgcolor: (theme) =>
                      theme.palette.mode === "dark"
                        ? "rgba(144, 202, 249, 0.16)"
                        : "rgba(25, 118, 210, 0.08)",
                    borderRadius: 2,
                    border: "1px solid",
                    borderColor: "secondary.main",
                  }}
                >
                  <Box>
                    <Typography
                      sx={{
                        fontWeight: 700,
                        fontSize: "1.05rem",
                        color: "secondary.main",
                      }}
                    >
                      Total Deductible Expenses
                    </Typography>
                    {hasServiceIncome && (
                      <Typography
                        variant='caption'
                        sx={{
                          display: "block",
                          mt: 0.5,
                          color: "secondary.main",
                          opacity: 0.8,
                        }}
                      >
                        *Allocated per income type (Service & Investment)
                      </Typography>
                    )}
                  </Box>
                  <Typography
                    sx={{
                      fontWeight: 800,
                      fontSize: "1.25rem",
                      color: "secondary.main",
                    }}
                  >
                    {formatAmount(finalAllowedExpenses)}
                  </Typography>
                </Box>
              </Box>

              {hasServiceIncome && (
                <Box
                  sx={{
                    mt: 3,
                    p: 2.5,
                    bgcolor: (theme) =>
                      alpha(theme.palette.warning.light, 0.15),
                    borderRadius: 1.5,
                    display: "flex",
                    gap: 1.5,
                    alignItems: "flex-start",
                  }}
                >
                  <InfoIcon
                    sx={{
                      fontSize: 20,
                      flexShrink: 0,
                      mt: 0.2,
                      color: "text.secondary",
                    }}
                  />
                  <Typography
                    variant='body2'
                    color='text.secondary'
                    sx={{ lineHeight: 1.6 }}
                  >
                    <strong>Note on Allocation:</strong> Service income utilizes
                    full common expenses, while other income types are subject
                    to the capped amount. The total above reflects the sum of
                    these distinct allocations, which may differ from simply
                    adding the global capped deduction to statutory expenses.
                  </Typography>
                </Box>
              )}
            </Paper>
          </>
        )}
      </ScheduleContainer>

      <Divider sx={{ my: 3 }} />

      {deductibleAllocationRows.length > 0 && (
        <DeductibleExpenseSummary
          deductibleAllocationRows={deductibleAllocationRowsForDisplay}
          finalAllowedExpenses={finalAllowedExpenses}
          totalDeductibleExpenses={displayedDeductibleTotal}
          deductibleRowOffsets={deductibleRowOffsets}
          rowOffsetKey={rowOffsetKey}
          onDeductibleRowOffsetChange={(key, value) => {
            setDeductibleRowOffsets((prev) => ({
              ...prev,
              [key]: clampRowOffset(value),
            }));
          }}
          canEdit={canEditSchedule}
          yearOfAssessment={company.yearOfAssessment}
          displayAndFinalTotalMismatch={displayAndFinalTotalMismatch}
        />
      )}

      <ScheduleNotes
        notes={notes}
        setNotes={setNotes}
        reviewNotes={reviewNotes}
        setReviewNotes={setReviewNotes}
        enableEdit={canEditSchedule}
      />
    </Box>
  );
};

export default AllocationExpenseSchedule;
